import { useQuery } from "@tanstack/react-query";
import { DynamicGrid } from "../../dynamicGrid";
import { Drawer } from "../../shared/Drawer";
import { assessmentQueryOptions } from "../trustAPI";
import { AwaitingResponseDrawerContent, MakeAllCustomAnswersStandardDialog } from "./AwaitingResponseDrawerContent";
import { ResponsedDrawerContent } from "./ResponsedDrawerContent";
import { logError } from "../../api/logError";
import { useState } from "react";
import { type AnswerRequest } from "../../dynamicForm";
import { useMutation } from "@tanstack/react-query";
import { updateStandardAnswersMutationOptions } from "../trustAPI";
import { Navigate } from "react-router";
import { TRUST_PATHS } from "../paths";

type SaveAsStandardAnswersDialogState = { isOpen: true; customAnswerRequests: AnswerRequest[] } | { isOpen: false };

export const AssessmentDrawer = () => {
  const selectedRowId = DynamicGrid.useSelectedRowId();
  const assessmentQuery = useQuery(assessmentQueryOptions(selectedRowId));
  const updateStandardAnswersMutation = useMutation(updateStandardAnswersMutationOptions());
  const [saveAsStandardAnswersDialogState, setSaveAsStandardAnswersDialogState] =
    useState<SaveAsStandardAnswersDialogState>({ isOpen: false });

  return (
    <>
      <DynamicGrid.Drawer sx={{ pb: 0 }}>
        {(() => {
          if (!assessmentQuery.data) return <Drawer.Loading />;
          switch (assessmentQuery.data.status) {
            case "awaiting-response":
              return (
                <AwaitingResponseDrawerContent
                  assessment={assessmentQuery.data}
                  onSignSuccess={({ standardAnswers, customAnswerRequests }) => {
                    if (standardAnswers.length > 0) return;
                    setSaveAsStandardAnswersDialogState({ isOpen: true, customAnswerRequests });
                  }}
                />
              );

            case "responded":
              return <ResponsedDrawerContent assessment={assessmentQuery.data} />;
            default:
              logError(
                new Error(
                  `A trust assessment with status ${assessmentQuery.data.status} should never be displayed in the drawer `
                )
              );
              return <Navigate to={TRUST_PATHS.grid()} />;
          }
        })()}
      </DynamicGrid.Drawer>

      {saveAsStandardAnswersDialogState.isOpen && (
        <MakeAllCustomAnswersStandardDialog
          onCancel={() => setSaveAsStandardAnswersDialogState({ isOpen: false })}
          onConfirm={async () => {
            await updateStandardAnswersMutation.mutateAsync(saveAsStandardAnswersDialogState.customAnswerRequests);
            setSaveAsStandardAnswersDialogState({ isOpen: false });
          }}
        />
      )}
    </>
  );
};
